// storage.js — frontend storage wrapper (client half of the Storage→GCS migration).
//
// Mirrors the db.js pattern: call sites swap `supabase.storage.from(b)` for these
// helpers with no behavior change, and a per-bucket flip list decides whether a
// bucket goes to GCS (via the storage-sign endpoint) or stays on Supabase.
//
// Why a backend hop at all: GCS has no per-object RLS for browsers. To upload, the
// browser asks storage-sign for a short-lived V4 signed PUT URL, then PUTs the
// bytes straight to GCS. To read, it asks for a signed GET URL. storage-sign is
// the isolation boundary (forces the practice_id key prefix from the JWT).
//
// Flipping a bucket = adding it to CLOUD_STORAGE_BUCKETS here AND to the backend
// lib/storage-allowlist.js (security review). Buckets not in the set keep the
// existing Supabase Storage path unchanged.

import { supabase } from './supabase';
import { apiFetch, API_BASE } from './api';

// ─── THE FLIP LIST. A bucket here reads/writes GCS through storage-sign. ───
const CLOUD_STORAGE_BUCKETS = new Set([
  'patient-photos',
  'provider-photos',
  'imaging',
  'dicom-files',
  'fax-documents',   // browser reads only; the fax functions write via the GCS SDK
  'scribe-audio',    // browser uploads the visit recording; transcribe-audio reads + deletes it
]);

// Demo mode has no Supabase session → no JWT for the gateway. Demo keeps the
// Supabase path (its own seed data) — never route it to GCS.
const DEMO_MODE = process.env.REACT_APP_DEMO_MODE === 'true';

function useCloud(bucket) {
  return !DEMO_MODE && !!API_BASE && CLOUD_STORAGE_BUCKETS.has(bucket);
}

// POST a sign request to the storage-sign endpoint; returns the signed URL string.
async function sign(op, bucket, key, { contentType, expiresIn } = {}) {
  const res = await apiFetch('storage-sign', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ op, bucket, key, contentType, expiresIn }),
  });
  let json = null;
  try { json = await res.json(); } catch { /* fall through to error below */ }
  if (!res.ok || !json || json.error || !json.url) {
    throw new Error(json?.error?.message || `storage-sign failed (HTTP ${res.status})`);
  }
  return json.url;
}

// Upload a Blob/File to `key`. Returns the stored bare key on success.
// GCS path: sign a PUT URL, then PUT the bytes. Supabase path: unchanged upsert.
export async function uploadFile(bucket, key, body, contentType) {
  if (!useCloud(bucket)) {
    const { error } = await supabase.storage.from(bucket).upload(key, body, {
      upsert: true,
      contentType,
    });
    if (error) throw error;
    return key;
  }
  const url = await sign('upload', bucket, key, { contentType });
  const put = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body,
  });
  if (!put.ok) throw new Error(`storage upload failed (HTTP ${put.status})`);
  return key;
}

// Resolve a signed download URL for an object key (empty string if none).
export async function getSignedUrl(bucket, key, expiresIn = 3600) {
  if (!key) return '';
  if (!useCloud(bucket)) {
    const { data, error } = await supabase.storage.from(bucket).createSignedUrl(key, expiresIn);
    if (error) throw error;
    return data?.signedUrl || '';
  }
  return sign('download', bucket, key, { expiresIn });
}

export const storage = { uploadFile, getSignedUrl };
export default storage;
